import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99998;
  animation: ${fadeIn} 0.2s ease-out forwards;
`;

export const ModalPanel = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 560px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 64px);
  max-height: calc(100dvh - 64px);
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${scaleIn} 0.2s ease-out forwards;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.lg} 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  flex-shrink: 0;
`;

export const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.space.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.radius.regular};
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

export const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.lg} 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
  flex-shrink: 0;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Input = styled.input<{ hasError?: boolean }>`
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  font-size: 14px;
  border: 1px solid ${({ hasError, theme }) =>
    hasError ? '#d32f2f' : theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.primary};
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const TextArea = styled.textarea<{ hasError?: boolean }>`
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  font-size: 14px;
  font-family: inherit;
  border: 1px solid ${({ hasError, theme }) =>
    hasError ? '#d32f2f' : theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.primary};
  outline: none;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.span`
  font-size: 12px;
  color: #d32f2f;
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.md};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const PhotoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

export const PhotoHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const PhotoPreviewRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  flex-wrap: wrap;
`;

export const PhotoPreviewContainer = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
`;

export const PhotoPreviewImg = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.regular};
`;

export const RemovePhotoButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
  padding: 0;
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const AddPhotoButton = styled.button`
  width: 64px;
  height: 64px;
  border: 2px dashed ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 24px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => theme.space['2xl']};
  gap: ${({ theme }) => theme.space.md};
  min-height: 260px;
`;

export const StatusIcon = styled.div<{ variant: 'success' | 'error' }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ variant }) => (variant === 'success' ? '#e8f5e9' : '#ffebee')};
  color: ${({ variant }) => (variant === 'success' ? '#2e7d32' : '#d32f2f')};

  svg {
    width: 32px;
    height: 32px;
  }
`;

export const StatusTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const StatusMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

export const RetryButton = styled.button`
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.xl}`};
  font-size: 14px;
  font-weight: 600;
  background: none;
  border: 2px solid ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

export const SubmitButton = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.pallete.secondary.color};
  color: ${({ theme }) => theme.colors.pallete.secondary.contrast};
  border: none;
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
